/**
 * Copy for the action row and action canvas.
 *
 * This module is deliberately independent from the existing action-copy
 * catalog. The UI can adopt this API without coupling its copy migration to
 * the current action summary implementation.
 */
export type ThemeChoice = 'dark' | 'light' | 'system';
export type ActionSurfaceLocale =
  | 'bg'
  | 'cs'
  | 'da'
  | 'de'
  | 'el'
  | 'en'
  | 'es'
  | 'et'
  | 'fi'
  | 'fr'
  | 'ga'
  | 'hr'
  | 'hu'
  | 'it'
  | 'lt'
  | 'lv'
  | 'mt'
  | 'nl'
  | 'pl'
  | 'pt'
  | 'ro'
  | 'sk'
  | 'sl'
  | 'sv';

type ThemeCopy = Record<ThemeChoice, string>;
type Template = (value: string | number) => string;

export type ActionSurfaceCopy = {
  pendingTheme: ThemeCopy;
  receiptTheme: ThemeCopy;
  languagePending: Template;
  languageReceipt: Template;
  timezonePending: Template;
  timezoneReceipt: Template;
  modelPending: Template;
  modelReceipt: Template;
  rememberPending: Template;
  rememberReceipt: Template;
  forgetPending: Template;
  forgetReceipt: Template;
  approvalNeeded: string;
  approvalWaiting: string;
  approvalExpired: string;
  expiredDetail: string;
  expiresAt: Template;
  expiresIn: Template;
  waitingFor: Template;
  loading: string;
  denied: string;
  uncertain: string;
  askAgain: string;
  writeNewRequest: string;
  askAgainPrefilled: string;
  askAgainDraftKept: string;
  checkAgain: string;
  details: string;
  undo: string;
  undone: string;
  undoFailed: string;
  manage: string;
  placeholder: string;
  pendingFooter: string;
  editingFooter: string;
  saveChanges: string;
  tabs: {
    draft: string;
    details: string;
    setup: string;
    schedule: string;
    preview: string;
  };
  primaryVerbs: {
    createSkill: string;
    updateSkill: string;
    createAgent: string;
    saveResult: string;
    suggestKnowledge: string;
    suggestPage: string;
    buildDocument: string;
  };
  artifact: {
    skill: { ready: string; saved: string };
    agent: { ready: string; saved: string };
    result: { ready: string; saved: string };
    knowledge: { ready: string; saved: string };
  };
  approvalCount: Template;
  showMore: Template;
  waitingCount: Template;
  afterUndo: Template;
  crossDevice: string;
};

type Vocabulary = {
  change: string;
  theme: string;
  dark: string;
  light: string;
  system: string;
  language: string;
  timezone: string;
  model: string;
  remember: string;
  forget: string;
  approve: string;
  approvalWaiting: string;
  approvalExpired: string;
  expiredDetail: string;
  approval: string;
  expires: string;
  inWord: string;
  waiting: string;
  loading: string;
  denied: string;
  uncertain: string;
  askAgain: string;
  writeNewRequest: string;
  askAgainPrefilled: string;
  askAgainDraftKept: string;
  buildPrimary: string;
  check: string;
  details: string;
  undo: string;
  undone: string;
  failed: string;
  manage: string;
  approveWord: string;
  pending: string;
  editing: string;
  save: string;
  draft: string;
  setup: string;
  schedule: string;
  preview: string;
  create: string;
  update: string;
  agent: string;
  result: string;
  knowledge: string;
  page: string;
  ready: string;
  saved: string;
  more: string;
  forWord: string;
  changes: string;
  across: string;
};

const english: Vocabulary = {
  change: 'Change',
  theme: 'theme',
  dark: 'dark',
  light: 'light',
  system: 'system',
  language: 'language',
  timezone: 'time zone',
  model: 'model',
  remember: 'Remember',
  forget: 'Forget',
  approve: 'Approval needed',
  approvalWaiting: 'Waiting for your approval',
  approvalExpired: 'Approval expired',
  expiredDetail: 'This approval expired, so nothing was saved.',
  approval: 'approval',
  expires: 'Expires',
  inWord: 'in',
  waiting: 'Waiting for',
  loading: 'Loading',
  denied: 'Denied',
  uncertain: 'We couldn’t confirm whether this went through.',
  askAgain: 'Ask again',
  writeNewRequest: 'Write a new request',
  askAgainPrefilled: 'Your earlier request is in the message box. Edit it if you like, then send.',
  askAgainDraftKept: 'Your draft is kept. Add your request and send.',
  buildPrimary: 'Build document',
  check: 'Check again',
  details: 'Details',
  undo: 'Undo',
  undone: 'Undone',
  failed: 'Undo failed',
  manage: 'Manage',
  approveWord: 'approve',
  pending: 'Nothing is saved until you approve.',
  editing: 'You are editing a draft.',
  save: 'Save changes',
  draft: 'Draft',
  setup: 'Setup',
  schedule: 'Schedule',
  preview: 'Preview',
  create: 'Create',
  update: 'Update',
  agent: 'agent',
  result: 'result',
  knowledge: 'knowledge',
  page: 'page',
  ready: 'Ready to',
  saved: 'Saved',
  more: 'more',
  forWord: 'for',
  changes: 'changes',
  across: 'available on your other devices',
};

const swedish: Vocabulary = {
  change: 'Ändra',
  theme: 'tema',
  dark: 'mörkt',
  light: 'ljust',
  system: 'systemets',
  language: 'språk',
  timezone: 'tidszon',
  model: 'modell',
  remember: 'Kom ihåg',
  forget: 'Glöm',
  approve: 'Godkännande krävs',
  approvalWaiting: 'Väntar på ditt godkännande',
  approvalExpired: 'Godkännandet har gått ut',
  expiredDetail: 'Godkännandet gick ut, så ingenting sparades.',
  approval: 'godkännande',
  expires: 'Går ut',
  inWord: 'om',
  waiting: 'Väntar på',
  loading: 'Laddar',
  denied: 'Nekat',
  uncertain: 'Vi kunde inte bekräfta om det gick igenom.',
  askAgain: 'Fråga igen',
  writeNewRequest: 'Skriv en ny begäran',
  askAgainPrefilled:
    'Din tidigare begäran finns i meddelanderutan. Redigera den om du vill och skicka sedan.',
  askAgainDraftKept: 'Ditt utkast finns kvar. Lägg till din begäran och skicka.',
  buildPrimary: 'Bygg dokument',
  check: 'Försök igen',
  details: 'Detaljer',
  undo: 'Ångra',
  undone: 'Ångrat',
  failed: 'Åtgärden kunde inte ångras',
  manage: 'Hantera',
  approveWord: 'godkänn',
  pending: 'Inget sparas innan du godkänner.',
  editing: 'Du redigerar ett utkast.',
  save: 'Spara ändringar',
  draft: 'Utkast',
  setup: 'Konfiguration',
  schedule: 'Schema',
  preview: 'Förhandsvisning',
  create: 'Skapa',
  update: 'Uppdatera',
  agent: 'agent',
  result: 'resultat',
  knowledge: 'kunskap',
  page: 'sida',
  ready: 'Redo att',
  saved: 'Sparat',
  more: 'fler',
  forWord: 'för',
  changes: 'ändringar',
  across: 'tillgängligt på dina andra enheter',
};

// Machine-drafted vocabulary. Each locale has a complete vocabulary so no
// locale silently displays an English placeholder. Swedish and English above
// are reviewed source copy; these rows remain marked for human review.
const machineDrafts: Record<Exclude<ActionSurfaceLocale, 'en' | 'sv'>, Vocabulary> = {
  bg: {
    change: 'Промени',
    theme: 'тема',
    dark: 'тъмен',
    light: 'светъл',
    system: 'системен',
    language: 'език',
    timezone: 'часова зона',
    model: 'модел',
    remember: 'Запомни',
    forget: 'Забрави',
    approve: 'Нужно е одобрение',
    approvalWaiting: 'Чака вашето одобрение',
    approvalExpired: 'Одобрението е изтекло',
    expiredDetail: 'Това одобрение изтече, затова нищо не е запазено.',
    approval: 'одобрение',
    expires: 'Изтича',
    inWord: 'след',
    waiting: 'Изчаква',
    loading: 'Зареждане',
    denied: 'Отказано',
    uncertain: 'Не успяхме да потвърдим дали това е минало.',
    askAgain: 'Попитайте отново',
    writeNewRequest: 'Напишете нова заявка',
    askAgainPrefilled:
      'Предишната ви заявка е в полето за съобщение. Редактирайте я, ако желаете, и я изпратете.',
    askAgainDraftKept: 'Черновата ви е запазена. Добавете заявката си и изпратете.',
    buildPrimary: 'Изгради документ',
    check: 'Провери отново',
    details: 'Подробности',
    undo: 'Отмени',
    undone: 'Отменено',
    failed: 'Отмяната неуспешна',
    manage: 'Управление',
    approveWord: 'одобри',
    pending: 'Нищо не се записва, докато не одобриш.',
    editing: 'Редактираш чернова.',
    save: 'Запази промените',
    draft: 'Чернова',
    setup: 'Настройка',
    schedule: 'График',
    preview: 'Преглед',
    create: 'Създай',
    update: 'Обнови',
    agent: 'агент',
    result: 'резултат',
    knowledge: 'знания',
    page: 'страница',
    ready: 'Готово да',
    saved: 'Запазено',
    more: 'още',
    forWord: 'за',
    changes: 'промени',
    across: 'достъпно на другите ти устройства',
  },
  cs: {
    change: 'Změnit',
    theme: 'motiv',
    dark: 'tmavý',
    light: 'světlý',
    system: 'systémový',
    language: 'jazyk',
    timezone: 'časové pásmo',
    model: 'model',
    remember: 'Zapamatovat',
    forget: 'Zapomenout',
    approve: 'Vyžaduje schválení',
    approvalWaiting: 'Čeká na vaše schválení',
    approvalExpired: 'Schválení vypršelo',
    expiredDetail: 'Toto schválení vypršelo, takže nic nebylo uloženo.',
    approval: 'schválení',
    expires: 'Vyprší',
    inWord: 'za',
    waiting: 'Čeká na',
    loading: 'Načítání',
    denied: 'Zamítnuto',
    uncertain: 'Nepodařilo se ověřit, zda to proběhlo.',
    askAgain: 'Zeptat se znovu',
    writeNewRequest: 'Napsat nový požadavek',
    askAgainPrefilled:
      'Váš předchozí požadavek je v poli zprávy. Pokud chcete, upravte ho a pak odešlete.',
    askAgainDraftKept: 'Váš koncept zůstává uložený. Doplňte požadavek a odešlete.',
    buildPrimary: 'Vytvořit dokument',
    check: 'Zkontrolovat znovu',
    details: 'Podrobnosti',
    undo: 'Vrátit',
    undone: 'Vráceno',
    failed: 'Vrácení se nezdařilo',
    manage: 'Spravovat',
    approveWord: 'schválit',
    pending: 'Nic se neuloží, dokud to neschválíš.',
    editing: 'Upravuješ koncept.',
    save: 'Uložit změny',
    draft: 'Koncept',
    setup: 'Nastavení',
    schedule: 'Plán',
    preview: 'Náhled',
    create: 'Vytvořit',
    update: 'Aktualizovat',
    agent: 'agenta',
    result: 'výsledek',
    knowledge: 'znalosti',
    page: 'stránku',
    ready: 'Připraveno k',
    saved: 'Uloženo',
    more: 'další',
    forWord: 'pro',
    changes: 'změny',
    across: 'dostupné na dalších zařízeních',
  },
  da: {
    change: 'Skift',
    theme: 'tema',
    dark: 'mørkt',
    light: 'lyst',
    system: 'systemets',
    language: 'sprog',
    timezone: 'tidszone',
    model: 'model',
    remember: 'Husk',
    forget: 'Glem',
    approve: 'Godkendelse kræves',
    approvalWaiting: 'Venter på din godkendelse',
    approvalExpired: 'Godkendelsen er udløbet',
    expiredDetail: 'Denne godkendelse er udløbet, så intet blev gemt.',
    approval: 'godkendelse',
    expires: 'Udløber',
    inWord: 'om',
    waiting: 'Venter på',
    loading: 'Indlæser',
    denied: 'Afvist',
    uncertain: 'Vi kunne ikke bekræfte, om det gik igennem.',
    askAgain: 'Spørg igen',
    writeNewRequest: 'Skriv en ny anmodning',
    askAgainPrefilled:
      'Din tidligere anmodning står i beskedfeltet. Ret den, hvis du vil, og send den så.',
    askAgainDraftKept: 'Din kladde er gemt. Tilføj din anmodning, og send.',
    buildPrimary: 'Byg dokument',
    check: 'Prøv igen',
    details: 'Detaljer',
    undo: 'Fortryd',
    undone: 'Fortrudt',
    failed: 'Fortrydelse mislykkedes',
    manage: 'Administrer',
    approveWord: 'godkend',
    pending: 'Intet gemmes, før du godkender.',
    editing: 'Du redigerer en kladde.',
    save: 'Gem ændringer',
    draft: 'Kladde',
    setup: 'Opsætning',
    schedule: 'Plan',
    preview: 'Forhåndsvisning',
    create: 'Opret',
    update: 'Opdater',
    agent: 'agent',
    result: 'resultat',
    knowledge: 'viden',
    page: 'side',
    ready: 'Klar til',
    saved: 'Gemt',
    more: 'flere',
    forWord: 'for',
    changes: 'ændringer',
    across: 'tilgængeligt på dine andre enheder',
  },
  de: {
    change: 'Ändern',
    theme: 'Design',
    dark: 'dunkel',
    light: 'hell',
    system: 'System',
    language: 'Sprache',
    timezone: 'Zeitzone',
    model: 'Modell',
    remember: 'Merken',
    forget: 'Vergessen',
    approve: 'Bestätigung erforderlich',
    approvalWaiting: 'Wartet auf Ihre Freigabe',
    approvalExpired: 'Freigabe abgelaufen',
    expiredDetail: 'Diese Freigabe ist abgelaufen, daher wurde nichts gespeichert.',
    approval: 'Bestätigung',
    expires: 'Läuft ab',
    inWord: 'in',
    waiting: 'Wartet auf',
    loading: 'Wird geladen',
    denied: 'Abgelehnt',
    uncertain: 'Wir konnten nicht bestätigen, ob es geklappt hat.',
    askAgain: 'Erneut fragen',
    writeNewRequest: 'Neue Anfrage schreiben',
    askAgainPrefilled:
      'Ihre frühere Anfrage steht im Nachrichtenfeld. Bearbeiten Sie sie bei Bedarf und senden Sie sie dann ab.',
    askAgainDraftKept:
      'Ihr Entwurf bleibt erhalten. Ergänzen Sie Ihre Anfrage und senden Sie sie ab.',
    buildPrimary: 'Dokument erstellen',
    check: 'Erneut prüfen',
    details: 'Details',
    undo: 'Rückgängig',
    undone: 'Rückgängig gemacht',
    failed: 'Rückgängig fehlgeschlagen',
    manage: 'Verwalten',
    approveWord: 'bestätigen',
    pending: 'Es wird nichts gespeichert, bevor du bestätigst.',
    editing: 'Du bearbeitest einen Entwurf.',
    save: 'Änderungen speichern',
    draft: 'Entwurf',
    setup: 'Einrichtung',
    schedule: 'Zeitplan',
    preview: 'Vorschau',
    create: 'Erstellen',
    update: 'Aktualisieren',
    agent: 'Agenten',
    result: 'Ergebnis',
    knowledge: 'Wissen',
    page: 'Seite',
    ready: 'Bereit zum',
    saved: 'Gespeichert',
    more: 'mehr',
    forWord: 'für',
    changes: 'Änderungen',
    across: 'auf deinen anderen Geräten verfügbar',
  },
  el: {
    change: 'Αλλαγή',
    theme: 'θέμα',
    dark: 'σκούρο',
    light: 'φωτεινό',
    system: 'συστήματος',
    language: 'γλώσσα',
    timezone: 'ζώνη ώρας',
    model: 'μοντέλο',
    remember: 'Απομνημόνευση',
    forget: 'Ξέχνα',
    approve: 'Απαιτείται έγκριση',
    approvalWaiting: 'Αναμένει την έγκρισή σας',
    approvalExpired: 'Η έγκριση έληξε',
    expiredDetail: 'Αυτή η έγκριση έληξε, επομένως δεν αποθηκεύτηκε τίποτα.',
    approval: 'έγκριση',
    expires: 'Λήγει',
    inWord: 'σε',
    waiting: 'Αναμονή για',
    loading: 'Φόρτωση',
    denied: 'Απορρίφθηκε',
    uncertain: 'Δεν ήταν δυνατό να επιβεβαιώσουμε αν ολοκληρώθηκε.',
    askAgain: 'Ρωτήστε ξανά',
    writeNewRequest: 'Γράψτε νέο αίτημα',
    askAgainPrefilled:
      'Το προηγούμενο αίτημά σας βρίσκεται στο πλαίσιο μηνύματος. Επεξεργαστείτε το αν θέλετε και, στη συνέχεια, στείλτε το.',
    askAgainDraftKept: 'Το πρόχειρό σας διατηρείται. Προσθέστε το αίτημά σας και στείλτε το.',
    buildPrimary: 'Κατασκευή εγγράφου',
    check: 'Έλεγχος ξανά',
    details: 'Λεπτομέρειες',
    undo: 'Αναίρεση',
    undone: 'Αναιρέθηκε',
    failed: 'Η αναίρεση απέτυχε',
    manage: 'Διαχείριση',
    approveWord: 'έγκριση',
    pending: 'Τίποτα δεν αποθηκεύεται πριν εγκρίνεις.',
    editing: 'Επεξεργάζεσαι πρόχειρο.',
    save: 'Αποθήκευση αλλαγών',
    draft: 'Πρόχειρο',
    setup: 'Ρύθμιση',
    schedule: 'Πρόγραμμα',
    preview: 'Προεπισκόπηση',
    create: 'Δημιουργία',
    update: 'Ενημέρωση',
    agent: 'πράκτορα',
    result: 'αποτέλεσμα',
    knowledge: 'γνώση',
    page: 'σελίδα',
    ready: 'Έτοιμο για',
    saved: 'Αποθηκεύτηκε',
    more: 'περισσότερα',
    forWord: 'για',
    changes: 'αλλαγές',
    across: 'διαθέσιμο στις άλλες συσκευές σου',
  },
  es: {
    change: 'Cambiar',
    theme: 'tema',
    dark: 'oscuro',
    light: 'claro',
    system: 'sistema',
    language: 'idioma',
    timezone: 'zona horaria',
    model: 'modelo',
    remember: 'Recordar',
    forget: 'Olvidar',
    approve: 'Se necesita aprobación',
    approvalWaiting: 'Esperando tu aprobación',
    approvalExpired: 'Aprobación caducada',
    expiredDetail: 'Esta aprobación caducó, así que no se guardó nada.',
    approval: 'aprobación',
    expires: 'Caduca',
    inWord: 'en',
    waiting: 'Esperando a',
    loading: 'Cargando',
    denied: 'Denegado',
    uncertain: 'No pudimos confirmar si se completó.',
    askAgain: 'Volver a preguntar',
    writeNewRequest: 'Escribir una nueva solicitud',
    askAgainPrefilled:
      'Tu solicitud anterior está en el cuadro de mensaje. Edítala si quieres y envíala.',
    askAgainDraftKept: 'Tu borrador se conserva. Añade tu solicitud y envíala.',
    buildPrimary: 'Crear documento',
    check: 'Comprobar de nuevo',
    details: 'Detalles',
    undo: 'Deshacer',
    undone: 'Deshecho',
    failed: 'No se pudo deshacer',
    manage: 'Gestionar',
    approveWord: 'aprobar',
    pending: 'No se guarda nada hasta que lo apruebes.',
    editing: 'Estás editando un borrador.',
    save: 'Guardar cambios',
    draft: 'Borrador',
    setup: 'Configuración',
    schedule: 'Programación',
    preview: 'Vista previa',
    create: 'Crear',
    update: 'Actualizar',
    agent: 'agente',
    result: 'resultado',
    knowledge: 'conocimiento',
    page: 'página',
    ready: 'Listo para',
    saved: 'Guardado',
    more: 'más',
    forWord: 'para',
    changes: 'cambios',
    across: 'disponible en tus otros dispositivos',
  },
  et: {
    change: 'Muuda',
    theme: 'teema',
    dark: 'tume',
    light: 'hele',
    system: 'süsteemi',
    language: 'keel',
    timezone: 'ajavöönd',
    model: 'mudel',
    remember: 'Jäta meelde',
    forget: 'Unusta',
    approve: 'Kinnitamine on vajalik',
    approvalWaiting: 'Ootab sinu kinnitust',
    approvalExpired: 'Kinnitus on aegunud',
    expiredDetail: 'See kinnitus aegus, seega ei salvestatud midagi.',
    approval: 'kinnitamine',
    expires: 'Aegub',
    inWord: 'aja pärast',
    waiting: 'Ootab',
    loading: 'Laadimine',
    denied: 'Keelatud',
    uncertain: 'Me ei saanud kinnitada, kas see õnnestus.',
    askAgain: 'Küsige uuesti',
    writeNewRequest: 'Kirjutage uus päring',
    askAgainPrefilled:
      'Teie varasem päring on sõnumiväljal. Muutke seda soovi korral ja saatke siis ära.',
    askAgainDraftKept: 'Teie mustand on alles. Lisage oma päring ja saatke.',
    buildPrimary: 'Ehita dokument',
    check: 'Kontrolli uuesti',
    details: 'Üksikasjad',
    undo: 'Võta tagasi',
    undone: 'Tagasi võetud',
    failed: 'Tagasivõtmine ebaõnnestus',
    manage: 'Halda',
    approveWord: 'kinnita',
    pending: 'Midagi ei salvestata enne kinnitamist.',
    editing: 'Muudad mustandit.',
    save: 'Salvesta muudatused',
    draft: 'Mustand',
    setup: 'Seadistus',
    schedule: 'Ajakava',
    preview: 'Eelvaade',
    create: 'Loo',
    update: 'Uuenda',
    agent: 'agendi',
    result: 'tulemuse',
    knowledge: 'teadmise',
    page: 'lehe',
    ready: 'Valmis',
    saved: 'Salvestatud',
    more: 'veel',
    forWord: 'jaoks',
    changes: 'muudatusi',
    across: 'saadaval sinu teistes seadmetes',
  },
  fi: {
    change: 'Vaihda',
    theme: 'teema',
    dark: 'tumma',
    light: 'vaalea',
    system: 'järjestelmän',
    language: 'kieli',
    timezone: 'aikavyöhyke',
    model: 'malli',
    remember: 'Muista',
    forget: 'Unohda',
    approve: 'Hyväksyntä tarvitaan',
    approvalWaiting: 'Odottaa hyväksyntääsi',
    approvalExpired: 'Hyväksyntä on vanhentunut',
    expiredDetail: 'Tämä hyväksyntä vanheni, joten mitään ei tallennettu.',
    approval: 'hyväksyntä',
    expires: 'Vanhenee',
    inWord: 'kuluttua',
    waiting: 'Odottaa',
    loading: 'Ladataan',
    denied: 'Estetty',
    uncertain: 'Emme voineet vahvistaa, menikö tämä läpi.',
    askAgain: 'Kysy uudelleen',
    writeNewRequest: 'Kirjoita uusi pyyntö',
    askAgainPrefilled:
      'Aiempi pyyntösi on viestikentässä. Muokkaa sitä halutessasi ja lähetä se sitten.',
    askAgainDraftKept: 'Luonnoksesi on tallessa. Lisää pyyntösi ja lähetä.',
    buildPrimary: 'Rakenna dokumentti',
    check: 'Tarkista uudelleen',
    details: 'Tiedot',
    undo: 'Kumoa',
    undone: 'Kumottu',
    failed: 'Kumous epäonnistui',
    manage: 'Hallitse',
    approveWord: 'hyväksy',
    pending: 'Mitään ei tallenneta ennen hyväksyntääsi.',
    editing: 'Muokkaat luonnosta.',
    save: 'Tallenna muutokset',
    draft: 'Luonnos',
    setup: 'Asetukset',
    schedule: 'Aikataulu',
    preview: 'Esikatselu',
    create: 'Luo',
    update: 'Päivitä',
    agent: 'agentti',
    result: 'tulos',
    knowledge: 'tieto',
    page: 'sivu',
    ready: 'Valmis',
    saved: 'Tallennettu',
    more: 'lisää',
    forWord: 'kohteelle',
    changes: 'muutokset',
    across: 'saatavilla muilla laitteillasi',
  },
  fr: {
    change: 'Modifier',
    theme: 'thème',
    dark: 'sombre',
    light: 'clair',
    system: 'système',
    language: 'langue',
    timezone: 'fuseau horaire',
    model: 'modèle',
    remember: 'Mémoriser',
    forget: 'Oublier',
    approve: 'Approbation requise',
    approvalWaiting: 'En attente de votre approbation',
    approvalExpired: 'Approbation expirée',
    expiredDetail: 'Cette approbation a expiré, donc rien n’a été enregistré.',
    approval: 'approbation',
    expires: 'Expire',
    inWord: 'dans',
    waiting: 'En attente de',
    loading: 'Chargement',
    denied: 'Refusé',
    uncertain: 'Nous n’avons pas pu confirmer que l’action a abouti.',
    askAgain: 'Redemander',
    writeNewRequest: 'Écrire une nouvelle demande',
    askAgainPrefilled:
      'Votre demande précédente est dans la zone de message. Modifiez-la si vous le souhaitez, puis envoyez-la.',
    askAgainDraftKept: 'Votre brouillon est conservé. Ajoutez votre demande et envoyez-la.',
    buildPrimary: 'Créer le document',
    check: 'Réessayer',
    details: 'Détails',
    undo: 'Annuler',
    undone: 'Annulé',
    failed: "Échec de l'annulation",
    manage: 'Gérer',
    approveWord: 'approuver',
    pending: "Rien n'est enregistré avant votre approbation.",
    editing: 'Vous modifiez un brouillon.',
    save: 'Enregistrer les modifications',
    draft: 'Brouillon',
    setup: 'Configuration',
    schedule: 'Planification',
    preview: 'Aperçu',
    create: 'Créer',
    update: 'Mettre à jour',
    agent: "l'agent",
    result: 'résultat',
    knowledge: 'connaissances',
    page: 'page',
    ready: 'Prêt à',
    saved: 'Enregistré',
    more: 'plus',
    forWord: 'pour',
    changes: 'modifications',
    across: 'disponible sur vos autres appareils',
  },
  ga: {
    change: 'Athraigh',
    theme: 'téama',
    dark: 'dorcha',
    light: 'éadrom',
    system: 'an chórais',
    language: 'teanga',
    timezone: 'crios ama',
    model: 'múnla',
    remember: 'Meabhraigh',
    forget: 'Déan dearmad',
    approve: 'Tá ceadú ag teastáil',
    approvalWaiting: 'Ag fanacht le do cheadú',
    approvalExpired: 'Tá an ceadú imithe in éag',
    expiredDetail: 'Chuaigh an ceadú seo in éag, mar sin níor sábháladh aon rud.',
    approval: 'ceadú',
    expires: 'Rachaidh in éag',
    inWord: 'i',
    waiting: 'Ag fanacht le',
    loading: 'Ag lódáil',
    denied: 'Diúltaithe',
    uncertain: 'Níorbh fhéidir linn a dhearbhú ar éirigh leis seo.',
    askAgain: 'Fiafraigh arís',
    writeNewRequest: 'Scríobh iarratas nua',
    askAgainPrefilled:
      'Tá d’iarratas roimhe seo sa bhosca teachtaireachta. Cuir in eagar é más mian leat, ansin seol é.',
    askAgainDraftKept: 'Coinníodh do dhréacht. Cuir d’iarratas leis agus seol é.',
    buildPrimary: 'Tóg an doiciméad',
    check: 'Seiceáil arís',
    details: 'Sonraí',
    undo: 'Cealaigh',
    undone: 'Cealaithe',
    failed: 'Theip ar chealú',
    manage: 'Bainistigh',
    approveWord: 'ceadaigh',
    pending: 'Ní shábháiltear aon rud go dtí go gceadaíonn tú é.',
    editing: 'Tá dréacht á chur in eagar agat.',
    save: 'Sábháil athruithe',
    draft: 'Dréacht',
    setup: 'Socrú',
    schedule: 'Sceideal',
    preview: 'Réamhamharc',
    create: 'Cruthaigh',
    update: 'Nuashonraigh',
    agent: 'gníomhaire',
    result: 'toradh',
    knowledge: 'eolas',
    page: 'leathanach',
    ready: 'Réidh le',
    saved: 'Sábháilte',
    more: 'níos mó',
    forWord: 'do',
    changes: 'athruithe',
    across: 'ar fáil ar do ghléasanna eile',
  },
  hr: {
    change: 'Promijeni',
    theme: 'tema',
    dark: 'tamna',
    light: 'svijetla',
    system: 'sustava',
    language: 'jezik',
    timezone: 'vremenska zona',
    model: 'model',
    remember: 'Zapamti',
    forget: 'Zaboravi',
    approve: 'Potrebno odobrenje',
    approvalWaiting: 'Čeka vaše odobrenje',
    approvalExpired: 'Odobrenje je isteklo',
    expiredDetail: 'Ovo je odobrenje isteklo, pa ništa nije spremljeno.',
    approval: 'odobrenje',
    expires: 'Istječe',
    inWord: 'za',
    waiting: 'Čeka',
    loading: 'Učitavanje',
    denied: 'Odbijeno',
    uncertain: 'Nismo mogli potvrditi je li to prošlo.',
    askAgain: 'Pitajte ponovno',
    writeNewRequest: 'Napišite novi zahtjev',
    askAgainPrefilled:
      'Vaš raniji zahtjev nalazi se u polju za poruku. Uredite ga ako želite, a zatim pošaljite.',
    askAgainDraftKept: 'Vaš nacrt je sačuvan. Dodajte zahtjev i pošaljite.',
    buildPrimary: 'Izradi dokument',
    check: 'Provjeri ponovno',
    details: 'Pojedinosti',
    undo: 'Poništi',
    undone: 'Poništeno',
    failed: 'Poništavanje nije uspjelo',
    manage: 'Upravljaj',
    approveWord: 'odobri',
    pending: 'Ništa se ne sprema dok ne odobriš.',
    editing: 'Uređuješ skicu.',
    save: 'Spremi promjene',
    draft: 'Skica',
    setup: 'Postavljanje',
    schedule: 'Raspored',
    preview: 'Pretpregled',
    create: 'Izradi',
    update: 'Ažuriraj',
    agent: 'agenta',
    result: 'rezultat',
    knowledge: 'znanje',
    page: 'stranicu',
    ready: 'Spremno za',
    saved: 'Spremljeno',
    more: 'više',
    forWord: 'za',
    changes: 'promjene',
    across: 'dostupno na drugim uređajima',
  },
  hu: {
    change: 'Módosítás',
    theme: 'téma',
    dark: 'sötét',
    light: 'világos',
    system: 'rendszer',
    language: 'nyelv',
    timezone: 'időzóna',
    model: 'modell',
    remember: 'Megjegyzés',
    forget: 'Elfelejtés',
    approve: 'Jóváhagyás szükséges',
    approvalWaiting: 'Jóváhagyásra vár',
    approvalExpired: 'A jóváhagyás lejárt',
    expiredDetail: 'Ez a jóváhagyás lejárt, ezért semmi sem lett elmentve.',
    approval: 'jóváhagyás',
    expires: 'Lejár',
    inWord: 'múlva',
    waiting: 'Várakozás erre',
    loading: 'Betöltés',
    denied: 'Elutasítva',
    uncertain: 'Nem tudtuk megerősíteni, hogy sikerült-e.',
    askAgain: 'Kérdezzen újra',
    writeNewRequest: 'Új kérés írása',
    askAgainPrefilled:
      'Korábbi kérése az üzenetmezőben van. Ha szeretné, módosítsa, majd küldje el.',
    askAgainDraftKept: 'A piszkozata megmaradt. Adja hozzá a kérését, és küldje el.',
    buildPrimary: 'Dokumentum készítése',
    check: 'Ellenőrizd újra',
    details: 'Részletek',
    undo: 'Visszavonás',
    undone: 'Visszavonva',
    failed: 'A visszavonás sikertelen',
    manage: 'Kezelés',
    approveWord: 'jóváhagy',
    pending: 'Semmi sem kerül mentésre a jóváhagyásod előtt.',
    editing: 'Piszkozatot szerkesztesz.',
    save: 'Módosítások mentése',
    draft: 'Piszkozat',
    setup: 'Beállítás',
    schedule: 'Ütemezés',
    preview: 'Előnézet',
    create: 'Létrehozás',
    update: 'Frissítés',
    agent: 'ügynök',
    result: 'eredmény',
    knowledge: 'tudás',
    page: 'oldal',
    ready: 'Kész erre:',
    saved: 'Mentve',
    more: 'további',
    forWord: 'ehhez:',
    changes: 'módosítás',
    across: 'elérhető más eszközeiden',
  },
  it: {
    change: 'Cambia',
    theme: 'tema',
    dark: 'scuro',
    light: 'chiaro',
    system: 'sistema',
    language: 'lingua',
    timezone: 'fuso orario',
    model: 'modello',
    remember: 'Ricorda',
    forget: 'Dimentica',
    approve: 'Approvazione necessaria',
    approvalWaiting: 'In attesa della tua approvazione',
    approvalExpired: 'Approvazione scaduta',
    expiredDetail: 'Questa approvazione è scaduta, quindi non è stato salvato nulla.',
    approval: 'approvazione',
    expires: 'Scade',
    inWord: 'tra',
    waiting: 'In attesa di',
    loading: 'Caricamento',
    denied: 'Negato',
    uncertain: 'Non siamo riusciti a confermare se è andato a buon fine.',
    askAgain: 'Chiedi di nuovo',
    writeNewRequest: 'Scrivi una nuova richiesta',
    askAgainPrefilled:
      'La tua richiesta precedente è nella casella del messaggio. Modificala se vuoi, poi inviala.',
    askAgainDraftKept: 'La tua bozza è conservata. Aggiungi la richiesta e inviala.',
    buildPrimary: 'Crea documento',
    check: 'Controlla di nuovo',
    details: 'Dettagli',
    undo: 'Annulla',
    undone: 'Annullato',
    failed: 'Annullamento non riuscito',
    manage: 'Gestisci',
    approveWord: 'approva',
    pending: 'Nulla viene salvato prima della tua approvazione.',
    editing: 'Stai modificando una bozza.',
    save: 'Salva modifiche',
    draft: 'Bozza',
    setup: 'Configurazione',
    schedule: 'Programmazione',
    preview: 'Anteprima',
    create: 'Crea',
    update: 'Aggiorna',
    agent: 'agente',
    result: 'risultato',
    knowledge: 'conoscenza',
    page: 'pagina',
    ready: 'Pronto per',
    saved: 'Salvato',
    more: 'altro',
    forWord: 'per',
    changes: 'modifiche',
    across: 'disponibile sugli altri dispositivi',
  },
  lt: {
    change: 'Keisti',
    theme: 'tema',
    dark: 'tamsi',
    light: 'šviesi',
    system: 'sistemos',
    language: 'kalba',
    timezone: 'laiko juosta',
    model: 'modelis',
    remember: 'Prisiminti',
    forget: 'Pamiršti',
    approve: 'Reikia patvirtinimo',
    approvalWaiting: 'Laukia jūsų patvirtinimo',
    approvalExpired: 'Patvirtinimo galiojimas baigėsi',
    expiredDetail: 'Šio patvirtinimo galiojimas baigėsi, todėl nieko neišsaugota.',
    approval: 'patvirtinimas',
    expires: 'Baigia galioti',
    inWord: 'po',
    waiting: 'Laukiama',
    loading: 'Kraunama',
    denied: 'Atmesta',
    uncertain: 'Nepavyko patvirtinti, ar tai pavyko.',
    askAgain: 'Klausti dar kartą',
    writeNewRequest: 'Rašyti naują užklausą',
    askAgainPrefilled:
      'Ankstesnė jūsų užklausa yra pranešimo laukelyje. Jei norite, ją pataisykite ir išsiųskite.',
    askAgainDraftKept: 'Jūsų juodraštis išsaugotas. Įrašykite užklausą ir išsiųskite.',
    buildPrimary: 'Kurti dokumentą',
    check: 'Patikrinti dar kartą',
    details: 'Išsamiau',
    undo: 'Anuliuoti',
    undone: 'Anuliuota',
    failed: 'Anuliuoti nepavyko',
    manage: 'Tvarkyti',
    approveWord: 'patvirtink',
    pending: 'Nieko neišsaugoma, kol nepatvirtinsi.',
    editing: 'Redaguoji juodraštį.',
    save: 'Išsaugoti pakeitimus',
    draft: 'Juodraštis',
    setup: 'Sąranka',
    schedule: 'Tvarkaraštis',
    preview: 'Peržiūra',
    create: 'Sukurti',
    update: 'Atnaujinti',
    agent: 'agentą',
    result: 'rezultatą',
    knowledge: 'žinias',
    page: 'puslapį',
    ready: 'Paruošta',
    saved: 'Išsaugota',
    more: 'daugiau',
    forWord: 'skirta',
    changes: 'pakeitimų',
    across: 'pasiekiama kituose įrenginiuose',
  },
  lv: {
    change: 'Mainīt',
    theme: 'motīvs',
    dark: 'tumšs',
    light: 'gaišs',
    system: 'sistēmas',
    language: 'valoda',
    timezone: 'laika josla',
    model: 'modelis',
    remember: 'Atcerēties',
    forget: 'Aizmirst',
    approve: 'Nepieciešams apstiprinājums',
    approvalWaiting: 'Gaida jūsu apstiprinājumu',
    approvalExpired: 'Apstiprinājums ir beidzies',
    expiredDetail: 'Šis apstiprinājums ir beidzies, tāpēc nekas netika saglabāts.',
    approval: 'apstiprinājums',
    expires: 'Beidzas',
    inWord: 'pēc',
    waiting: 'Gaida',
    loading: 'Ielādē',
    denied: 'Noraidīts',
    uncertain: 'Mēs nevarējām apstiprināt, vai tas izdevās.',
    askAgain: 'Jautāt vēlreiz',
    writeNewRequest: 'Rakstīt jaunu pieprasījumu',
    askAgainPrefilled:
      'Jūsu iepriekšējais pieprasījums ir ziņojuma laukā. Ja vēlaties, rediģējiet to un pēc tam nosūtiet.',
    askAgainDraftKept: 'Jūsu melnraksts ir saglabāts. Pievienojiet pieprasījumu un nosūtiet.',
    buildPrimary: 'Veidot dokumentu',
    check: 'Pārbaudīt vēlreiz',
    details: 'Informācija',
    undo: 'Atsaukt',
    undone: 'Atsaukts',
    failed: 'Neizdevās atsaukt',
    manage: 'Pārvaldīt',
    approveWord: 'apstiprini',
    pending: 'Nekas netiek saglabāts, kamēr neapstiprināsi.',
    editing: 'Rediģē melnrakstu.',
    save: 'Saglabāt izmaiņas',
    draft: 'Melnraksts',
    setup: 'Iestatīšana',
    schedule: 'Grafiks',
    preview: 'Priekšskatījums',
    create: 'Izveidot',
    update: 'Atjaunināt',
    agent: 'aģentu',
    result: 'rezultātu',
    knowledge: 'zināšanas',
    page: 'lapu',
    ready: 'Gatavs',
    saved: 'Saglabāts',
    more: 'vairāk',
    forWord: 'paredzēts',
    changes: 'izmaiņas',
    across: 'pieejams citās ierīcēs',
  },
  mt: {
    change: 'Ibdel',
    theme: 'tema',
    dark: 'skur',
    light: 'ċar',
    system: 'sistema',
    language: 'lingwa',
    timezone: 'żona tal-ħin',
    model: 'mudell',
    remember: 'Ftakar',
    forget: 'Tinsa',
    approve: 'Hemm bżonn approvazzjoni',
    approvalWaiting: 'Qed jistenna l-approvazzjoni tiegħek',
    approvalExpired: 'L-approvazzjoni skadiet',
    expiredDetail: 'Din l-approvazzjoni skadiet, għalhekk ma nżamm xejn.',
    approval: 'approvazzjoni',
    expires: 'Jiskadi',
    inWord: 'fi',
    waiting: 'Qed jistenna',
    loading: 'Qed jitgħabba',
    denied: 'Miċħud',
    uncertain: 'Ma stajniex nikkonfermaw jekk għaddiex.',
    askAgain: 'Erġa’ staqsi',
    writeNewRequest: 'Ikteb talba ġdida',
    askAgainPrefilled:
      'It-talba li għamilt qabel qiegħda fil-kaxxa tal-messaġġ. Editjaha jekk trid, imbagħad ibgħatha.',
    askAgainDraftKept: 'L-abbozz tiegħek inżamm. Żid it-talba tiegħek u ibgħat.',
    buildPrimary: 'Ibni d-dokument',
    check: 'Erġa’ ċċekkja',
    details: 'Dettalji',
    undo: 'Neħħi',
    undone: 'Imneħħi',
    failed: 'It-tneħħija falliet',
    manage: 'Immaniġġja',
    approveWord: 'approva',
    pending: 'Xejn ma jiġi ssejvjat qabel ma tapprova.',
    editing: 'Qed teditja abbozz.',
    save: 'Issejvja l-bidliet',
    draft: 'Abbozz',
    setup: 'Twaqqif',
    schedule: 'Skeda',
    preview: 'Previżjoni',
    create: 'Oħloq',
    update: 'Aġġorna',
    agent: 'aġent',
    result: 'riżultat',
    knowledge: 'għarfien',
    page: 'paġna',
    ready: 'Lest biex',
    saved: 'Issejvjat',
    more: 'aktar',
    forWord: 'għal',
    changes: 'bidliet',
    across: 'disponibbli fuq it-tagħmir l-ieħor tiegħek',
  },
  nl: {
    change: 'Wijzig',
    theme: 'thema',
    dark: 'donker',
    light: 'licht',
    system: 'systeem',
    language: 'taal',
    timezone: 'tijdzone',
    model: 'model',
    remember: 'Onthouden',
    forget: 'Vergeten',
    approve: 'Goedkeuring nodig',
    approvalWaiting: 'Wacht op je goedkeuring',
    approvalExpired: 'Goedkeuring verlopen',
    expiredDetail: 'Deze goedkeuring is verlopen, dus er is niets opgeslagen.',
    approval: 'goedkeuring',
    expires: 'Verloopt',
    inWord: 'over',
    waiting: 'Wachten op',
    loading: 'Laden',
    denied: 'Geweigerd',
    uncertain: 'We konden niet bevestigen of het is gelukt.',
    askAgain: 'Opnieuw vragen',
    writeNewRequest: 'Nieuw verzoek schrijven',
    askAgainPrefilled:
      'Je eerdere verzoek staat in het berichtvak. Pas het aan als je wilt en verstuur het dan.',
    askAgainDraftKept: 'Je concept blijft bewaard. Voeg je verzoek toe en verstuur het.',
    buildPrimary: 'Document maken',
    check: 'Opnieuw controleren',
    details: 'Details',
    undo: 'Ongedaan maken',
    undone: 'Ongedaan gemaakt',
    failed: 'Ongedaan maken mislukt',
    manage: 'Beheren',
    approveWord: 'goedkeuren',
    pending: 'Er wordt niets opgeslagen voordat je goedkeurt.',
    editing: 'Je bewerkt een concept.',
    save: 'Wijzigingen opslaan',
    draft: 'Concept',
    setup: 'Instellen',
    schedule: 'Planning',
    preview: 'Voorbeeld',
    create: 'Maken',
    update: 'Bijwerken',
    agent: 'agent',
    result: 'resultaat',
    knowledge: 'kennis',
    page: 'pagina',
    ready: 'Klaar om',
    saved: 'Opgeslagen',
    more: 'meer',
    forWord: 'voor',
    changes: 'wijzigingen',
    across: 'beschikbaar op je andere apparaten',
  },
  pl: {
    change: 'Zmień',
    theme: 'motyw',
    dark: 'ciemny',
    light: 'jasny',
    system: 'systemowy',
    language: 'język',
    timezone: 'strefa czasowa',
    model: 'model',
    remember: 'Zapamiętaj',
    forget: 'Zapomnij',
    approve: 'Wymagana zgoda',
    approvalWaiting: 'Czeka na Twoje zatwierdzenie',
    approvalExpired: 'Zatwierdzenie wygasło',
    expiredDetail: 'To zatwierdzenie wygasło, więc nic nie zostało zapisane.',
    approval: 'zgoda',
    expires: 'Wygasa',
    inWord: 'za',
    waiting: 'Oczekiwanie na',
    loading: 'Ładowanie',
    denied: 'Odrzucono',
    uncertain: 'Nie udało się potwierdzić, czy to się udało.',
    askAgain: 'Zapytaj ponownie',
    writeNewRequest: 'Napisz nową prośbę',
    askAgainPrefilled:
      'Twoja wcześniejsza prośba jest w polu wiadomości. Jeśli chcesz, zmień ją, a potem wyślij.',
    askAgainDraftKept: 'Twoja wersja robocza została zachowana. Dodaj prośbę i wyślij.',
    buildPrimary: 'Utwórz dokument',
    check: 'Sprawdź ponownie',
    details: 'Szczegóły',
    undo: 'Cofnij',
    undone: 'Cofnięto',
    failed: 'Nie udało się cofnąć',
    manage: 'Zarządzaj',
    approveWord: 'zatwierdź',
    pending: 'Nic nie zostanie zapisane, dopóki nie zatwierdzisz.',
    editing: 'Edytujesz wersję roboczą.',
    save: 'Zapisz zmiany',
    draft: 'Wersja robocza',
    setup: 'Konfiguracja',
    schedule: 'Harmonogram',
    preview: 'Podgląd',
    create: 'Utwórz',
    update: 'Zaktualizuj',
    agent: 'agenta',
    result: 'wynik',
    knowledge: 'wiedzę',
    page: 'stronę',
    ready: 'Gotowe, aby',
    saved: 'Zapisano',
    more: 'więcej',
    forWord: 'dla',
    changes: 'zmian',
    across: 'dostępne na innych urządzeniach',
  },
  pt: {
    change: 'Alterar',
    theme: 'tema',
    dark: 'escuro',
    light: 'claro',
    system: 'sistema',
    language: 'idioma',
    timezone: 'fuso horário',
    model: 'modelo',
    remember: 'Lembrar',
    forget: 'Esquecer',
    approve: 'É necessária aprovação',
    approvalWaiting: 'A aguardar a sua aprovação',
    approvalExpired: 'Aprovação expirada',
    expiredDetail: 'Esta aprovação expirou, por isso nada foi guardado.',
    approval: 'aprovação',
    expires: 'Expira',
    inWord: 'em',
    waiting: 'A aguardar',
    loading: 'A carregar',
    denied: 'Recusado',
    uncertain: 'Não foi possível confirmar se foi concluído.',
    askAgain: 'Perguntar novamente',
    writeNewRequest: 'Escrever um novo pedido',
    askAgainPrefilled:
      'O seu pedido anterior está na caixa de mensagem. Edite-o se quiser e depois envie.',
    askAgainDraftKept: 'O seu rascunho foi guardado. Acrescente o seu pedido e envie.',
    buildPrimary: 'Criar documento',
    check: 'Verificar novamente',
    details: 'Detalhes',
    undo: 'Anular',
    undone: 'Anulado',
    failed: 'Falha ao anular',
    manage: 'Gerir',
    approveWord: 'aprovar',
    pending: 'Nada é guardado até aprovares.',
    editing: 'Estás a editar um rascunho.',
    save: 'Guardar alterações',
    draft: 'Rascunho',
    setup: 'Configuração',
    schedule: 'Agendamento',
    preview: 'Pré-visualização',
    create: 'Criar',
    update: 'Atualizar',
    agent: 'agente',
    result: 'resultado',
    knowledge: 'conhecimento',
    page: 'página',
    ready: 'Pronto para',
    saved: 'Guardado',
    more: 'mais',
    forWord: 'para',
    changes: 'alterações',
    across: 'disponível nos teus outros dispositivos',
  },
  ro: {
    change: 'Schimbă',
    theme: 'temă',
    dark: 'închis',
    light: 'deschis',
    system: 'sistem',
    language: 'limbă',
    timezone: 'fus orar',
    model: 'model',
    remember: 'Memorează',
    forget: 'Uitǎ',
    approve: 'Este necesară aprobarea',
    approvalWaiting: 'Așteaptă aprobarea ta',
    approvalExpired: 'Aprobarea a expirat',
    expiredDetail: 'Această aprobare a expirat, așa că nu s-a salvat nimic.',
    approval: 'aprobare',
    expires: 'Expiră',
    inWord: 'în',
    waiting: 'Se așteaptă',
    loading: 'Se încarcă',
    denied: 'Respins',
    uncertain: 'Nu am putut confirma dacă a reușit.',
    askAgain: 'Întrebați din nou',
    writeNewRequest: 'Scrieți o solicitare nouă',
    askAgainPrefilled:
      'Solicitarea anterioară este în caseta de mesaj. Editați-o dacă doriți, apoi trimiteți-o.',
    askAgainDraftKept: 'Ciorna este păstrată. Adăugați solicitarea și trimiteți.',
    buildPrimary: 'Creare document',
    check: 'Verifică din nou',
    details: 'Detalii',
    undo: 'Anulează',
    undone: 'Anulat',
    failed: 'Anularea a eșuat',
    manage: 'Gestionează',
    approveWord: 'aprobă',
    pending: 'Nimic nu se salvează înainte de aprobare.',
    editing: 'Editezi o schiță.',
    save: 'Salvează modificările',
    draft: 'Schiță',
    setup: 'Configurare',
    schedule: 'Programare',
    preview: 'Previzualizare',
    create: 'Creează',
    update: 'Actualizează',
    agent: 'agent',
    result: 'rezultat',
    knowledge: 'cunoștințe',
    page: 'pagină',
    ready: 'Gata pentru',
    saved: 'Salvat',
    more: 'mai multe',
    forWord: 'pentru',
    changes: 'modificări',
    across: 'disponibil pe celelalte dispozitive',
  },
  sk: {
    change: 'Zmeniť',
    theme: 'motív',
    dark: 'tmavý',
    light: 'svetlý',
    system: 'systémový',
    language: 'jazyk',
    timezone: 'časové pásmo',
    model: 'model',
    remember: 'Zapamätať',
    forget: 'Zabudnúť',
    approve: 'Vyžaduje sa schválenie',
    approvalWaiting: 'Čaká na vaše schválenie',
    approvalExpired: 'Schválenie vypršalo',
    expiredDetail: 'Toto schválenie vypršalo, takže sa nič neuložilo.',
    approval: 'schválenie',
    expires: 'Vyprší',
    inWord: 'o',
    waiting: 'Čaká na',
    loading: 'Načítava sa',
    denied: 'Zamietnuté',
    uncertain: 'Nepodarilo sa overiť, či to prebehlo.',
    askAgain: 'Opýtať sa znova',
    writeNewRequest: 'Napísať novú požiadavku',
    askAgainPrefilled:
      'Vaša predchádzajúca požiadavka je v poli správy. Ak chcete, upravte ju a potom odošlite.',
    askAgainDraftKept: 'Váš koncept zostáva uložený. Doplňte požiadavku a odošlite.',
    buildPrimary: 'Vytvoriť dokument',
    check: 'Skontrolovať znova',
    details: 'Podrobnosti',
    undo: 'Vrátiť',
    undone: 'Vrátené',
    failed: 'Vrátenie zlyhalo',
    manage: 'Spravovať',
    approveWord: 'schváliť',
    pending: 'Nič sa neuloží, kým to neschváliš.',
    editing: 'Upravuješ koncept.',
    save: 'Uložiť zmeny',
    draft: 'Koncept',
    setup: 'Nastavenie',
    schedule: 'Plán',
    preview: 'Ukážka',
    create: 'Vytvoriť',
    update: 'Aktualizovať',
    agent: 'agenta',
    result: 'výsledok',
    knowledge: 'znalosti',
    page: 'stránku',
    ready: 'Pripravené na',
    saved: 'Uložené',
    more: 'ďalšie',
    forWord: 'pre',
    changes: 'zmeny',
    across: 'dostupné na iných zariadeniach',
  },
  sl: {
    change: 'Spremeni',
    theme: 'tema',
    dark: 'temna',
    light: 'svetla',
    system: 'sistema',
    language: 'jezik',
    timezone: 'časovni pas',
    model: 'model',
    remember: 'Zapomni si',
    forget: 'Pozabi',
    approve: 'Potrebna je odobritev',
    approvalWaiting: 'Čaka na vašo odobritev',
    approvalExpired: 'Odobritev je potekla',
    expiredDetail: 'Ta odobritev je potekla, zato ni bilo nič shranjeno.',
    approval: 'odobritev',
    expires: 'Poteče',
    inWord: 'čez',
    waiting: 'Čakanje na',
    loading: 'Nalaganje',
    denied: 'Zavrnjeno',
    uncertain: 'Nismo mogli potrditi, ali je uspelo.',
    askAgain: 'Vprašajte znova',
    writeNewRequest: 'Napišite novo zahtevo',
    askAgainPrefilled:
      'Vaša prejšnja zahteva je v polju za sporočilo. Po želji jo uredite in nato pošljite.',
    askAgainDraftKept: 'Vaš osnutek je shranjen. Dodajte zahtevo in pošljite.',
    buildPrimary: 'Izdelaj dokument',
    check: 'Preveri znova',
    details: 'Podrobnosti',
    undo: 'Razveljavi',
    undone: 'Razveljavljeno',
    failed: 'Razveljavitev ni uspela',
    manage: 'Upravljaj',
    approveWord: 'odobri',
    pending: 'Nič se ne shrani, dokler ne odobriš.',
    editing: 'Urejaš osnutek.',
    save: 'Shrani spremembe',
    draft: 'Osnutek',
    setup: 'Nastavitev',
    schedule: 'Razpored',
    preview: 'Predogled',
    create: 'Ustvari',
    update: 'Posodobi',
    agent: 'agenta',
    result: 'rezultat',
    knowledge: 'znanje',
    page: 'stran',
    ready: 'Pripravljeno za',
    saved: 'Shranjeno',
    more: 'več',
    forWord: 'za',
    changes: 'sprememb',
    across: 'na voljo v drugih napravah',
  },
};

const locales: Record<ActionSurfaceLocale, Vocabulary> = {
  en: english,
  sv: swedish,
  ...machineDrafts,
};

function makeCopy(v: Vocabulary, locale: ActionSurfaceLocale): ActionSurfaceCopy {
  const pendingTheme = {
    dark: `${v.change} ${v.theme} · ${v.dark}`,
    light: `${v.change} ${v.theme} · ${v.light}`,
    system: `${v.change} ${v.theme} · ${v.system}`,
  };
  const receiptTheme = {
    dark: `${v.theme} · ${v.dark}`,
    light: `${v.theme} · ${v.light}`,
    system: `${v.theme} · ${v.system}`,
  };
  const copy: ActionSurfaceCopy = {
    pendingTheme,
    receiptTheme,
    languagePending: (x) => `${v.change} ${v.language} · ${x}`,
    languageReceipt: (x) => `${v.language} · ${x}`,
    timezonePending: (x) => `${v.change} ${v.timezone} · ${x}`,
    timezoneReceipt: (x) => `${v.timezone} · ${x}`,
    modelPending: (x) => `${v.change} ${v.model} · ${x}`,
    modelReceipt: (x) => `${v.model} · ${x}`,
    rememberPending: (x) => `${v.remember} · ${x}`,
    rememberReceipt: (x) => `${v.remember} · ${x}`,
    forgetPending: (x) => `${v.forget} · ${x}`,
    forgetReceipt: (x) => `${v.forget} · ${x}`,
    approvalNeeded: v.approve,
    approvalWaiting: v.approvalWaiting,
    approvalExpired: v.approvalExpired,
    expiredDetail: v.expiredDetail,
    expiresAt: (x) => `${v.expires} ${x}`,
    expiresIn: (x) => `${v.expires} ${v.inWord} ${x}`,
    waitingFor: (x) => `${v.waiting} ${x}`,
    loading: v.loading,
    denied: v.denied,
    uncertain: v.uncertain,
    askAgain: v.askAgain,
    writeNewRequest: v.writeNewRequest,
    askAgainPrefilled: v.askAgainPrefilled,
    askAgainDraftKept: v.askAgainDraftKept,
    checkAgain: v.check,
    details: v.details,
    undo: v.undo,
    undone: v.undone,
    undoFailed: v.failed,
    manage: v.manage,
    placeholder: `${v.approveWord}…`,
    pendingFooter: v.pending,
    editingFooter: v.editing,
    saveChanges: v.save,
    tabs: {
      draft: v.draft,
      details: v.details,
      setup: v.setup,
      schedule: v.schedule,
      preview: v.preview,
    },
    primaryVerbs: {
      createSkill: `${v.create} Skill`,
      updateSkill: `${v.update} Skill`,
      createAgent: `${v.create} ${v.agent}`,
      saveResult: `${v.save} ${v.result}`,
      suggestKnowledge: `${v.create} ${v.knowledge}`,
      suggestPage: `${v.create} ${v.page}`,
      buildDocument: v.buildPrimary,
    },
    artifact: {
      skill: { ready: `${v.ready} Skill`, saved: `Skill · ${v.saved}` },
      agent: { ready: `${v.ready} ${v.agent}`, saved: `${v.agent} · ${v.saved}` },
      result: { ready: `${v.ready} ${v.result}`, saved: `${v.result} · ${v.saved}` },
      knowledge: { ready: `${v.ready} ${v.knowledge}`, saved: `${v.knowledge} · ${v.saved}` },
    },
    approvalCount: (x) => `${x} ${v.approval}`,
    showMore: (x) => `+${x} ${v.more}`,
    waitingCount: (x) => `${v.waiting} ${x}`,
    afterUndo: (x) => `${v.undone} · ${x}`,
    crossDevice: v.across,
  };
  if (locale === 'en') {
    return {
      ...copy,
      pendingTheme: {
        dark: 'Switch to dark mode?',
        light: 'Switch to light mode?',
        system: 'Follow your system theme?',
      },
      receiptTheme: {
        dark: 'Dark mode on',
        light: 'Light mode on',
        system: 'Theme follows your system',
      },
      languagePending: (x) => `Switch the app to ${x}?`,
      languageReceipt: (x) => `App language: ${x}`,
      timezonePending: (x) => `Set your time zone to ${x}?`,
      timezoneReceipt: (x) => `Time zone: ${x}`,
      modelPending: (x) => `Use ${x} for chats?`,
      modelReceipt: (x) => `Chat model: ${x}`,
      rememberPending: (x) => `Remember “${x}”?`,
      rememberReceipt: (x) => `Remembered “${x}”`,
      forgetPending: (x) => `Forget “${x}”?`,
      forgetReceipt: (x) => `Forgot “${x}”`,
      loading: 'Loading request…',
      uncertain: "Couldn't confirm the decision.",
      waitingFor: (x) => `Waiting for ${x}`,
      placeholder: 'Reply “approve” to approve, or ask something else…',
      approvalCount: (x) =>
        Number(x) === 1 ? '1 change needs your approval' : `${x} changes need your approval`,
      showMore: (x) => `Show ${x} more`,
      waitingCount: (x) => (Number(x) === 1 ? '1 approval waiting' : `${x} approvals waiting`),
      tabs: {
        draft: 'Draft',
        details: 'Details',
        setup: 'Setup',
        schedule: 'Schedule',
        preview: 'Preview',
      },
      primaryVerbs: {
        createSkill: 'Save draft',
        updateSkill: 'Apply changes',
        createAgent: 'Create agent',
        saveResult: 'Save result',
        suggestKnowledge: 'Submit suggestion',
        suggestPage: 'Submit change',
        buildDocument: 'Build document',
      },
      artifact: {
        skill: { ready: 'Skill draft ready', saved: 'Skill draft saved' },
        agent: { ready: 'Agent ready to create', saved: 'Agent created' },
        result: { ready: 'Result ready', saved: 'Result saved' },
        knowledge: { ready: 'Knowledge change ready', saved: 'Sent for review' },
      },
    };
  }
  if (locale === 'sv') {
    return {
      ...copy,
      pendingTheme: {
        dark: 'Byt till mörkt tema?',
        light: 'Byt till ljust tema?',
        system: 'Följ systemets tema?',
      },
      receiptTheme: {
        dark: 'Mörkt tema aktiverat',
        light: 'Ljust tema aktiverat',
        system: 'Temat följer systemet',
      },
      languagePending: (x) => `Byt appens språk till ${x}?`,
      languageReceipt: (x) => `Appspråk: ${x}`,
      timezonePending: (x) => `Ställ in tidszon till ${x}?`,
      timezoneReceipt: (x) => `Tidszon: ${x}`,
      modelPending: (x) => `Använd ${x} för chattar?`,
      modelReceipt: (x) => `Chattmodell: ${x}`,
      rememberPending: (x) => `Kom ihåg ”${x}”?`,
      rememberReceipt: (x) => `Kom ihåg ”${x}”`,
      forgetPending: (x) => `Glöm ”${x}”?`,
      forgetReceipt: (x) => `Glömde ”${x}”`,
      loading: 'Laddar begäran…',
      uncertain: 'Kunde inte bekräfta beslutet.',
      waitingFor: (x) => `Väntar på ${x}`,
      placeholder: 'Svara ”godkänn” för att godkänna, eller fråga något annat…',
      approvalCount: (x) => `${x} ändringar väntar på ditt godkännande`,
      showMore: (x) => `Visa ${x} till`,
      waitingCount: (x) => `${x} godkännanden väntar`,
      tabs: {
        draft: 'Utkast',
        details: 'Detaljer',
        setup: 'Konfiguration',
        schedule: 'Schema',
        preview: 'Förhandsvisning',
      },
      primaryVerbs: {
        createSkill: 'Spara utkast',
        updateSkill: 'Tillämpa ändringar',
        createAgent: 'Skapa agent',
        saveResult: 'Spara resultat',
        suggestKnowledge: 'Skicka förslag',
        suggestPage: 'Skicka ändring',
        buildDocument: 'Bygg dokument',
      },
      artifact: {
        skill: { ready: 'Skill-utkast klart', saved: 'Skill-utkast sparat' },
        agent: { ready: 'Agent redo att skapas', saved: 'Agent skapad' },
        result: { ready: 'Resultat klart', saved: 'Resultat sparat' },
        knowledge: { ready: 'Kunskapsändring klar', saved: 'Skickad för granskning' },
      },
    };
  }
  return copy;
}

const catalog: Record<ActionSurfaceLocale, ActionSurfaceCopy> = Object.fromEntries(
  Object.entries(locales).map(([locale, vocabulary]) => [
    locale,
    makeCopy(vocabulary, locale as ActionSurfaceLocale),
  ]),
) as Record<ActionSurfaceLocale, ActionSurfaceCopy>;

export function actionSurfaceCopy(locale: string): ActionSurfaceCopy {
  const language = locale.toLowerCase().split(/[-_]/, 1)[0] as ActionSurfaceLocale;
  return catalog[language] ?? catalog.en;
}

export const ACTION_SURFACE_LOCALES = Object.freeze(Object.keys(catalog) as ActionSurfaceLocale[]);
